import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, ScanText, Copy, Download, Upload, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { absoluteUrl, getPublicTool } from "@/lib/tools-catalog";
import { buildToolSoftwareApplicationJsonLd } from "@/lib/seo/tool-structured-data";

export const Route = createFileRoute("/tools/ocr")({
  head: () => {
    const tool = getPublicTool("ocr");
    const url = absoluteUrl(tool.path);
    const title = tool.pageTitle;
    const description = tool.description;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildToolSoftwareApplicationJsonLd(tool)),
        },
      ],
    };
  },
  component: OcrPage,
});

type Lang = "auto" | "en" | "af" | "zu" | "xh" | "st" | "tn";
type Captcha = { question: string; token: string };

const ACCEPTED_MIME = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_BYTES = 8 * 1024 * 1024;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result);
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

function OcrPage() {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [text, setText] = useState("");
  const [language, setLanguage] = useState<Lang>("auto");
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState<Captcha | null>(null);
  const [answer, setAnswer] = useState("");

  async function loadCaptcha() {
    try {
      const r = await fetch("/api/public/tools/ocr");
      if (!r.ok) throw new Error();
      setCaptcha((await r.json()) as Captcha);
      setAnswer("");
    } catch {
      toast.error("Couldn't load captcha — refresh and try again.");
    }
  }

  useEffect(() => {
    void loadCaptcha();
  }, []);

  function onPick(f: File | null) {
    setText("");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (!f) {
      setFile(null);
      setPreviewUrl("");
      return;
    }
    if (!ACCEPTED_MIME.includes(f.type.toLowerCase())) {
      toast.error("Please upload a JPEG, PNG or WebP image");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Image too large (max 8 MB)");
      return;
    }
    if (f.size < 200) {
      toast.error("This file looks empty");
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  }

  async function run() {
    if (!file) return toast.error("Upload an image first");
    if (!captcha) return toast.error("Captcha not loaded yet");
    if (!answer.trim()) return toast.error("Answer the captcha");

    setLoading(true);
    setText("");
    try {
      const fileBase64 = await fileToBase64(file);
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      const res = await fetch("/api/public/tools/ocr", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          fileBase64,
          mime: file.type,
          language,
          captchaToken: captcha.token,
          captchaAnswer: Number(answer),
        }),
      });
      const data = await res.json();
      if (data?.nextCaptcha) {
        setCaptcha(data.nextCaptcha as Captcha);
        setAnswer("");
      } else if (data?.needsNewCaptcha) {
        void loadCaptcha();
      }
      if (!res.ok) {
        toast.error(data?.error || "OCR failed");
        return;
      }
      setText(data.text || "");
      if (data.billed) toast.success(`Done — 1 credit used. Balance: ${data.balance ?? "—"}`);
      else if (data.text) toast.success("Done");
      else toast.info(data.error || "No text detected in the image");
    } catch (e: any) {
      toast.error(e?.message || "Network error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="container-narrow py-12">
      <p className="text-xs uppercase tracking-[0.25em] text-gold">Free tool</p>
      <h1 className="mt-2 font-display text-3xl text-navy md:text-4xl">OCR Scanner</h1>
      <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
        Upload an image of a document. First scan each day is free — after that, 1 credit per scan
        (sign in required). Files are sent to our AI Gateway for extraction only and are not stored.
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div>
          <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-input bg-card p-8 text-center text-sm text-muted-foreground hover:border-navy">
            <Upload className="mb-2 h-6 w-6 text-navy" />
            {file ? file.name : "Click to upload JPEG, PNG or WebP (≤8 MB)"}
            <input
              type="file"
              accept={ACCEPTED_MIME.join(",")}
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0] ?? null)}
            />
          </label>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Preview of uploaded document"
              className="mt-3 max-h-80 w-full rounded-md border border-border object-contain"
            />
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="text-xs text-muted-foreground">Language</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value as Lang)}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm"
            >
              <option value="auto">Auto-detect</option>
              <option value="en">English</option>
              <option value="af">Afrikaans</option>
              <option value="zu">isiZulu</option>
              <option value="xh">isiXhosa</option>
              <option value="st">Sesotho</option>
              <option value="tn">Setswana</option>
            </select>
          </div>

          <div className="mt-4 rounded-md border border-input bg-muted/30 p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-medium text-navy">
              <ShieldCheck className="h-4 w-4 text-gold" /> Human check
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-mono">{captcha?.question ?? "…"} =</span>
              <input
                type="number"
                inputMode="numeric"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm"
              />
              <button
                type="button"
                onClick={loadCaptcha}
                className="text-xs text-muted-foreground underline"
              >
                new
              </button>
            </div>
          </div>

          <button
            onClick={run}
            disabled={loading || !file || !captcha || !answer.trim()}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ScanText className="h-4 w-4" />
            )}
            Extract text
          </button>
        </div>

        <div>
          <label className="mb-2 block text-xs uppercase tracking-widest text-muted-foreground">
            Extracted text
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={20}
            placeholder="Extracted text appears here — you can edit before copying."
            className="w-full rounded-md border border-input bg-muted/30 p-3 text-sm"
          />
          {text && (
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(text);
                  toast.success("Copied");
                }}
                className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-2 text-xs"
              >
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
              <button
                onClick={() => {
                  const blob = new Blob([text], { type: "text/plain" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "extracted.txt";
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-2 text-xs"
              >
                <Download className="h-3.5 w-3.5" /> Download .txt
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
